// Redux Utils
import { useSelector, useDispatch } from "react-redux";

// React Utils
import React, { useState, useEffect, useCallback } from "react";

// Packages Components
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// Componentes
import Modal from "@/components/Modals/Modal";
import SignIn from "@/components/Forms/SignIn";
import Register from "@/components/Forms/Register";
import ActualizarPerfilYPostularATicketGratis from "@/components/Forms/ActualizarPerfilYPostularATicketGratis";
import PoliticasYPostularATicketGratis from "@/components/Forms/PoliticasYPostularATicketGratis";
import Tooltip from "@/components/Tooltip";

// Contextos
import { useCart } from "@/contexts/CartContext";

// Thunks
import {
  crearCarrito,
  addTicketsFromEventToExistingCart,
} from "@/features/carrito/carritoThunks";
import { crearSolicitudInvitacion } from "@/features/invitacion/invitacionThunks";

// Utils
import { getColorByEstadoInvitacion } from "@/utils/invitacionUtils";

// Router
import { Link, useNavigate } from "react-router-dom";

// Referral utilities
import { getReferralForEvent } from "@/utils/referralLinkManager";

export default function CardTickets({ evento, LogoPerfilEmpresa }) {
  // Redux State and Dispatch
  const dispatch = useDispatch();
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const { miSolicitudEnEvento } = useSelector((state) => state.invitacion);

  // Per-ticket invitation state: { [ticketId]: { loading, error, solicitudCreada } }
  const [invitacionState, setInvitacionState] = useState({});

  // Modal States
  const [openModalSignIn, setOpenModalSignIn] = useState(false);
  const [openModalRegister, setOpenModalRegister] = useState(false);
  const [
    openModalActualizarYPostularATicketGratis,
    setOpenModalActualizarYPostularATicketGratis,
  ] = useState(false);
  const [openModalPoliticasYPostular, setOpenModalPoliticasYPostular] =
    useState(false);
  const [avisoSolicitudEnviada, setAvisoSolicitudEnviada] = useState(false);
  const [openModalErrorReferido, setOpenModalErrorReferido] = useState(false);
  const [errorReferidoMessage, setErrorReferidoMessage] = useState("");
  const [showPerfilModal, setShowPerfilModal] = useState(false);
  const [camposFaltantes, setCamposFaltantes] = useState([]);

  const navigate = useNavigate();

  // Track ticket quantities
  const [ticketQuantities, setTicketQuantities] = useState({});

  // Get cart context
  const { getCartForEvent, reloadCarts, carts } = useCart();

  console.log("carts:", carts);

  const updateTicketQuantity = (ticketId, change) => {
    setTicketQuantities((prev) => {
      const currentQuantity = prev[ticketId] || 0;
      const newQuantity = Math.max(0, currentQuantity + change);

      // Find the ticket to check limits
      const ticket = evento.tickets.find((t) => t.id === ticketId);
      const maxAllowed = ticket?.limitePorPersona || 10;

      return {
        ...prev,
        [ticketId]: Math.min(newQuantity, maxAllowed),
      };
    });
  };

  // Helper functions that depend on state - use useCallback to prevent recreation
  const getSelectedTicketsForCart = useCallback(() => {
    if (!evento?.tickets) return [];

    console.log(
      "getSelectedTicketsForCart - ticketQuantities:",
      ticketQuantities,
    );
    console.log(
      "Calculating selected tickets for cart with quantities:",
      evento?.tickets,
    );
    return evento.tickets
      .filter((ticket) => !ticket.esInvitacion)
      .map((ticket) => ({
        idTipoTicket: ticket.id,
        cantidad: ticketQuantities[ticket.id] || 0,
        precioVenta: ticket.precio,
      }))
      .filter((ticket) => ticket.cantidad > 0);
  }, [ticketQuantities, evento]);

  const getTotalSelectedTickets = useCallback(() => {
    const total = Object.values(ticketQuantities).reduce(
      (total, qty) => total + qty,
      0,
    );
    console.log(
      "getTotalSelectedTickets - ticketQuantities:",
      ticketQuantities,
      "total:",
      total,
    );
    return total;
  }, [ticketQuantities]);

  // Handle solicitar invitacion for a specific ticket
  const handleSolicitarInvitacion = useCallback(async (ticket) => {
    if (!isAuthenticated) {
      setOpenModalSignIn(true);
      return;
    }

    const ticketId = ticket.id;

    setInvitacionState((prev) => ({
      ...prev,
      [ticketId]: { loading: true, error: null, solicitudCreada: prev[ticketId]?.solicitudCreada || null },
    }));

    try {
      const referralInfo = getReferralForEvent(evento.id);
      const ambassadorReferralCode = referralInfo?.code || null;
      const result = await dispatch(
        crearSolicitudInvitacion({
          uidEvento: evento.id,
          idTipoTicketInvitacion: ticketId,
          ambassadorReferralCode,
        })
      ).unwrap();
      setInvitacionState((prev) => ({
        ...prev,
        [ticketId]: { loading: false, error: null, solicitudCreada: result },
      }));
    } catch (err) {
      const status = err?.status || err?.statusCode;
      const esPerfilIncompleto =
        status === 422 ||
        err?.code === "PERFIL_INCOMPLETO" ||
        Array.isArray(err?.camposFaltantes);
      if (esPerfilIncompleto) {
        setCamposFaltantes(err?.camposFaltantes || []);
        setShowPerfilModal(true);
        setInvitacionState((prev) => ({
          ...prev,
          [ticketId]: { loading: false, error: null, solicitudCreada: null },
        }));
      } else {
        let mensaje;
        if (status === 409 || (typeof err === "string" && err.includes("409"))) {
          mensaje = "Ya tienes una solicitud activa para este evento.";
        } else {
          mensaje =
            err?.message || (typeof err === "string" ? err : "Error al solicitar la invitacion");
        }
        setInvitacionState((prev) => ({
          ...prev,
          [ticketId]: { loading: false, error: mensaje, solicitudCreada: null },
        }));
      }
    }
  }, [isAuthenticated, dispatch, evento?.id]);

  // Create stable add to cart function using useCallback
  const handleAddToCart = useCallback(async () => {
    const selectedTickets = getSelectedTicketsForCart();
    // Obtener código de referido si existe
    const referralInfo = getReferralForEvent(evento.id);
    // Verificar si hay código de referido
    const ambassadorReferralCode = referralInfo?.code || null;

    if (selectedTickets.length > 0) {
      try {
        console.log("id del evento", evento.id);
        // Verificar si ya existe un carrito para este evento
        const existingCart = getCartForEvent(evento.id);

        if (existingCart) {
          // Si existe un carrito, agregar tickets al carrito existente
          await dispatch(
            addTicketsFromEventToExistingCart({
              eventoUid: evento.id,
              carritoUid: existingCart.carritoUid,
              tickets: selectedTickets,
              ambassadorReferralCode,
            }),
          ).unwrap();
        } else {
          // Si no existe carrito, crear uno nuevo
          await dispatch(
            crearCarrito({
              eventoUid: evento.id,
              tickets: selectedTickets,
              ambassadorReferralCode,
            }),
          ).unwrap();
        }

        // Limpiar cantidades después de agregar al carrito
        console.log("Limpiando ticketQuantities...");
        setTicketQuantities({});
        console.log("ticketQuantities limpiado");

        // Recargar carritos
        reloadCarts();

        return true;
      } catch (error) {
        console.error("Error al agregar tickets al carrito:", error);

        // Manejo específico para el error de auto-referido
        if (error?.code === "SELF_REFERRAL") {
          // Limpiar localStorage del referido para este evento. 'ambassador_referrals' es el key que se está usando en localStorage, debería eliminar el objeto en 'ambassador_referrals' de solo el referido de este evento, busca el objeto por el el valor 'code' que coincide con el código de referido del evento actual.
          const storedReferrals = localStorage.getItem("ambassador_referrals");

          console.log("SELF_REFERRAL 1:", storedReferrals);
          if (storedReferrals) {
            console.log("SELF_REFERRAL 2");
            // Parsear el objeto de referidos
            const referralsObj = JSON.parse(storedReferrals);
            console.log("SELF_REFERRAL 3:", referralsObj);

            // Obtener el referido para este evento
            const referralForEvent = getReferralForEvent(evento.id);
            console.log("SELF_REFERRAL 4:", referralForEvent);

            // Si existe, eliminarlo del objeto y guardar de nuevo
            if (referralForEvent) {
              console.log(
                "SELF_REFERRAL 5: Removing referral code",
                referralForEvent.uid,
              );
              // Eliminar el referido de este evento
              delete referralsObj[evento.id];
              // Guardar el objeto actualizado en localStorage
              localStorage.setItem(
                "ambassador_referrals",
                JSON.stringify(referralsObj),
              );
              console.log(
                "SELF_REFERRAL 6: Updated referrals in localStorage",
                referralsObj,
              );
            }
          }

          // Mostrar modal de error con mensaje específico
          setErrorReferidoMessage(
            "Llegaste a este evento o estás intentando comprar tickets con tu propio link de referido. Esto no está permitido. Se eliminará el código de referido para que puedas intentar nuevamente sin referido o con otro código de referido.",
          );
          setOpenModalErrorReferido(true);
        } else if (error?.code === "INVALID_REFERRAL") {
          setErrorReferidoMessage(
            "Llegaste a este evento con un código de referido no válido para este evento.",
          );
          setOpenModalErrorReferido(true);
        }

        return false;
      }
    }
    return false;
  }, [
    getSelectedTicketsForCart,
    evento,
    dispatch,
    reloadCarts,
    getCartForEvent,
  ]);

  // Notify parent component about ticket changes
  useEffect(() => {
    if (
      evento?.onTicketsChange &&
      typeof evento.onTicketsChange === "function"
    ) {
      const hasTickets = getTotalSelectedTickets() > 0;
      const totalTickets = getTotalSelectedTickets();

      console.log(
        "Notifying parent - hasTickets:",
        hasTickets,
        "totalTickets:",
        totalTickets,
        "ticketQuantities:",
        ticketQuantities,
      );

      evento.onTicketsChange({
        hasTickets,
        totalTickets,
        addToCart: handleAddToCart,
      });
    }
  }, [getTotalSelectedTickets, evento?.onTicketsChange, ticketQuantities]); // Added ticketQuantities to dependencies

  return (
    <>
      <div className="bg-aw-bluedark-95 rounded-2xl sm:p-6 w-full flex flex-col min-h-fit xl:min-h-[80dvh]">
        <div className="text-white font-bold text-lg mb-4">
          Obtén Tu Ticket Aquí!
          <p className="text-yellow-500 italic font-semibold text-xs">
            Nota: Todos los tickets quedarán asociados a tu RUT
          </p>
        </div>
        {evento?.tickets?.map((ticket, i) => {
          // Format price display
          const formatPrice = (price) => {
            if (!price || price === 0) return "GRATIS";
            return new Intl.NumberFormat("es-CL", {
              style: "currency",
              currency: "CLP",
              minimumFractionDigits: 0,
            }).format(price);
          };

          // Get ticket image - use custom image if available and not using event image, otherwise use event image
          const getTicketImage = () => {
            if (ticket.useImgEvento === 0 && ticket.imgTicket) {
              return ticket.imgTicket;
            }
            return evento.img;
          };

          // Use the backend esInvitacion flag to determine invitation tickets
          const isInvitation = !!ticket.esInvitacion;

          // Invitation-specific derived state
          const invState = invitacionState[ticket.id] || {};
          const solicitudActiva = invState.solicitudCreada || miSolicitudEnEvento;
          const solicitudEsDeEsteTicket =
            solicitudActiva?.idTipoTicketInvitacion === ticket.id ||
            solicitudActiva?.tipoTicketId === ticket.id ||
            // If miSolicitudEnEvento exists and there's only one invitation type, apply it
            (isInvitation && solicitudActiva && !invState.solicitudCreada);

          return (
            <div
              key={i}
              className="flex items-center gap-3 bg-aw-bluedark rounded-xl p-3 mb-3 border border-aw-green relative"
            >
              <img
                src={getTicketImage()}
                alt={ticket.customTypeName}
                className="w-16 h-16 rounded-lg object-cover"
              />
              <div className="flex-1">
                <p className="text-white font-bold text-sm">
                  Ticket
                  <span className="block">{ticket.customTypeName}</span>
                </p>
                <div className="text-aw-green font-bold text-lg">
                  <span className="inline-block mr-1">
                    {isInvitation ? "Solo Invitacion" : formatPrice(ticket.precio)}
                  </span>
                  <span className="text-gray-400 font-normal text-xs block sm:inline-block">
                    {ticket.ticketGrupal
                      ? `para ${ticket.cantidadPersonasTicketGrupal} personas`
                      : "/ por persona"}
                  </span>
                </div>
              </div>
              {/* Action area — only visible while the event hasn't ended */}
              {new Date(evento.fechaHoraFin) > new Date() && (
                <div className="flex flex-col gap-1 items-end mt-1">
                  {isInvitation ? (
                    // ----- INVITATION TICKET ACTIONS -----
                    (() => {
                      // Show existing solicitud state
                      if (solicitudEsDeEsteTicket && solicitudActiva && solicitudActiva.estado !== "rechazada") {
                        const colores = getColorByEstadoInvitacion(solicitudActiva.estado);
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colores.badge}`}>
                              {colores.label}
                            </span>
                            {solicitudActiva.uid && user?.uid && (
                              <Link
                                to={`/perfil/${user.uid}/invitaciones/${solicitudActiva.uid}`}
                                className="text-xs text-aw-green underline"
                              >
                                Ver solicitud
                              </Link>
                            )}
                          </div>
                        );
                      }

                      // Show error if any
                      if (invState.error) {
                        return (
                          <div className="flex flex-col items-end gap-1">
                            <span className="text-xs text-red-400 text-right max-w-[140px]">
                              {invState.error}
                            </span>
                            <button
                              onClick={() => handleSolicitarInvitacion(ticket)}
                              className="border border-aw-green text-aw-green px-3 py-1 rounded-full text-xs font-bold hover:bg-aw-green hover:text-aw-bluedark transition"
                            >
                              Reintentar
                            </button>
                          </div>
                        );
                      }

                      // Show solicitar button
                      return (
                        <button
                          onClick={() => handleSolicitarInvitacion(ticket)}
                          disabled={invState.loading}
                          className={`border border-aw-green text-aw-green px-3 py-1 rounded-full text-xs font-bold transition flex items-center gap-1 ${
                            invState.loading
                              ? "opacity-50 cursor-not-allowed"
                              : "hover:bg-aw-green hover:text-aw-bluedark"
                          }`}
                        >
                          {invState.loading ? (
                            <>
                              <FontAwesomeIcon icon="fa-solid fa-spinner" className="animate-spin" />
                              Enviando...
                            </>
                          ) : (
                            "Postularme"
                          )}
                        </button>
                      );
                    })()
                  ) : carts.some((cart) =>
                      cart?.tickets.some(
                        (tckt) => tckt.tipoTicketId == ticket.id,
                      ),
                    ) ? (
                    <Tooltip
                      content="Ya has agregado este ticket al carrito o ya has comprado el límite permitido por persona"
                      position={`${
                        window.innerWidth < 640 ? "center_xs" : "center"
                      }`}
                    >
                      <div className="flex items-center gap-2 opacity-50 cursor-not-allowed bg-aw-green-30 rounded-2xl px-2 py-1">
                        <button
                          disabled
                          className="w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg opacity-50 cursor-not-allowed"
                        >
                          -
                        </button>
                        <span className="text-white font-bold min-w-[2rem] text-center">
                          {ticketQuantities[ticket.id] || 0}
                        </span>
                        <button
                          disabled
                          className="w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg opacity-50 cursor-not-allowed"
                        >
                          +
                        </button>
                      </div>
                    </Tooltip>
                  ) : (
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setOpenModalSignIn(true);
                          } else {
                            updateTicketQuantity(ticket.id, -1);
                          }
                        }}
                        disabled={(ticketQuantities[ticket.id] || 0) === 0}
                        className={`w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg transition ${
                          (ticketQuantities[ticket.id] || 0) === 0
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-aw-green hover:text-aw-bluedark"
                        }`}
                      >
                        -
                      </button>
                      <span className="text-white font-bold min-w-[2rem] text-center">
                        {ticketQuantities[ticket.id] || 0}
                      </span>
                      <button
                        onClick={() => {
                          if (!isAuthenticated) {
                            setOpenModalSignIn(true);
                          } else {
                            updateTicketQuantity(ticket.id, 1);
                          }
                        }}
                        disabled={
                          (ticketQuantities[ticket.id] || 0) >=
                          (ticket.limitePorPersona || 10)
                        }
                        className={`w-7 h-7 rounded-full bg-aw-bluedark text-aw-green border border-aw-green flex items-center justify-center font-bold text-lg transition ${
                          (ticketQuantities[ticket.id] || 0) >=
                          (ticket.limitePorPersona || 10)
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:bg-aw-green hover:text-aw-bluedark"
                        }`}
                      >
                        +
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {/* Modals - outside the map */}
        <Modal
          isOpen={openModalActualizarYPostularATicketGratis}
          onClose={() => setOpenModalActualizarYPostularATicketGratis(false)}
          large="max-w-4xl"
        >
          <ActualizarPerfilYPostularATicketGratis
            LogoEmpresa={LogoPerfilEmpresa}
            openPoliticasYPostular={() => {
              setOpenModalActualizarYPostularATicketGratis(false);
              setTimeout(() => {
                setOpenModalPoliticasYPostular(true);
              }, 300);
            }}
          />
        </Modal>
        <Modal
          isOpen={openModalPoliticasYPostular}
          onClose={() => setOpenModalPoliticasYPostular(false)}
          large="max-w-4xl"
        >
          <PoliticasYPostularATicketGratis
            LogoEmpresa={LogoPerfilEmpresa}
            openSolicitudEnviada={() => {
              setOpenModalPoliticasYPostular(false);
              setTimeout(() => {
                setAvisoSolicitudEnviada(true);
              }, 300);
            }}
          />
        </Modal>

        <Modal
          isOpen={avisoSolicitudEnviada}
          onClose={() => setAvisoSolicitudEnviada(false)}
          large="max-w-xl"
        >
          <div className="p-6 text-center bg-aw-bluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
            <div className="text-center">
              <FontAwesomeIcon
                icon={"fa-solid fa-circle-check"}
                className="text-aw-green text-6xl mb-4 mx-auto"
              />
              <h2 className="text-2xl font-semibold mb-2 text-white">
                Postulación Enviada
              </h2>
              <p className="text-gray-400 mb-4">
                ¡Gracias por postular al ticket gratis! Hemos recibido tu
                solicitud y nuestro equipo la está revisando.
              </p>
              {/* TODO: Agregar la información condicional según el exito o rechazo de la solicitud y colocar los CTA que se requieran */}
            </div>
          </div>
        </Modal>

        {/* Modal: perfil incompleto */}
        <Modal
          isOpen={showPerfilModal}
          onClose={() => setShowPerfilModal(false)}
          large="max-w-md"
        >
          <div className="mx-auto p-6 bg-aw-bgbluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green text-center">
            <FontAwesomeIcon
              icon="fa-solid fa-user-circle"
              className="text-5xl text-aw-green mb-4 mx-auto"
            />
            <h3 className="text-lg font-bold text-white mb-2">
              Perfil incompleto
            </h3>
            <p className="text-sm text-gray-300 mb-4">
              Para solicitar una invitacion necesitas tener configurado en tu perfil:
            </p>
            <ul className="text-sm text-left mb-5 space-y-2">
              {camposFaltantes.includes("numeroTributario") && (
                <li className="flex items-center gap-2 text-red-400">
                  <FontAwesomeIcon icon="fa-solid fa-circle-xmark" />
                  <span>Numero tributario (RUT)</span>
                </li>
              )}
              {camposFaltantes.includes("instagram") && (
                <li className="flex items-center gap-2 text-red-400">
                  <FontAwesomeIcon icon="fa-solid fa-circle-xmark" />
                  <span>Usuario de Instagram</span>
                </li>
              )}
            </ul>
            <p className="text-xs text-gray-400 mb-5">
              Dirigete a tu perfil, a la pestaña{" "}
              <span className="text-aw-green font-semibold">"Informacion"</span>
              , para completar estos datos.
            </p>
            <button
              onClick={() => {
                setShowPerfilModal(false);
                navigate(`/perfil/${user?.uid}`);
              }}
              className="px-5 py-2 bg-aw-green text-aw-bluedark rounded-full font-bold text-sm hover:scale-105 transition-transform"
            >
              Ir a mi Perfil
            </button>
          </div>
        </Modal>

        <Modal
          isOpen={openModalErrorReferido}
          onClose={() => setOpenModalErrorReferido(false)}
          large="max-w-xl"
        >
          <div className="p-6 text-center bg-aw-bluedark rounded-lg inset-shadow-sm inset-shadow-aw-green shadow-sm shadow-aw-green">
            <div className="text-center">
              <FontAwesomeIcon
                icon={"fa-solid fa-circle-exclamation"}
                className="text-red-500 text-6xl mb-4 mx-auto"
              />
              <h2 className="text-2xl font-semibold mb-2 text-white">
                Error con Código de Referido
              </h2>
              <p className="text-gray-300 mb-6">{errorReferidoMessage}</p>
              <button
                onClick={() => setOpenModalErrorReferido(false)}
                className="bg-aw-green text-aw-bluedark px-6 py-2 rounded-full font-bold hover:bg-aw-green-70 transition"
              >
                Entendido
              </button>
            </div>
          </div>
        </Modal>
      </div>

      {!isAuthenticated && (
        <>
          <Modal
            isOpen={openModalSignIn}
            onClose={() => setOpenModalSignIn(false)}
            large="max-w-4xl"
          >
            <SignIn
              onRegisterClick={() => {
                setOpenModalSignIn(false);
                setTimeout(() => setOpenModalRegister(true), 300);
              }}
            />
          </Modal>
          <Modal
            isOpen={openModalRegister}
            onClose={() => setOpenModalRegister(false)}
            large="max-w-4xl"
          >
            <Register
              onBackToSignInClick={() => {
                setOpenModalSignIn(true);
                setTimeout(() => setOpenModalRegister(false), 300);
              }}
            />
          </Modal>
        </>
      )}
    </>
  );
}
